import classNames from "@/utils/classNames";
import {
    FloatingArrow,
    FloatingPortal,
    Placement,
    arrow,
    autoUpdate,
    flip,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    useRole,
} from "@floating-ui/react";
import { PropsWithChildren, ReactNode, useRef, useState } from "react";

export type TooltipProps = PropsWithChildren<{
    initiallyOpen?: boolean;
    controlledOpen?: boolean;
    title: ReactNode;
    position?: Placement;
    trigger?: "mouseenter" | "click";
    disabled?: boolean;
    wrapperContainerClassName?: string;
    popupContainerClassName?: string;
    bgColor?: string;
    showWhenScrolledAway?: boolean; // If an element would be scrolled out of view, show the tooltip at the edge of the window
    rootElement?: HTMLElement;
    onOpen?: () => void;
    interactive?: boolean;
}>;

export default function Tooltip({
    initiallyOpen = false,
    controlledOpen,
    children,
    title,
    position = "bottom",
    trigger = "mouseenter",
    disabled,
    wrapperContainerClassName = "",
    popupContainerClassName = "",
    bgColor = "#333333",
    showWhenScrolledAway,
    rootElement,
    onOpen,
    interactive,
}: TooltipProps) {
    const isControlled = controlledOpen !== undefined;
    const [uncontrolledOpen, setUncontrolledOpen] = useState(initiallyOpen);
    const isOpen = isControlled ? controlledOpen : uncontrolledOpen;

    const handleOpenChange = (open: boolean) => {
        if (isControlled) {
            return;
        }
        setUncontrolledOpen(open);
        if (open && onOpen) {
            onOpen();
        }
    };
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: handleOpenChange,
        placement: position,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(10),
            flip({ fallbackAxisSideDirection: "start" }),
            shift({
                padding: 15,
                ...(showWhenScrolledAway
                    ? {
                          crossAxis: true,
                          altBoundary: true,
                      }
                    : null),
            }),
            arrow({
                element: arrowRef,
            }),
        ],
    });

    const enableHover = trigger === "mouseenter" && !disabled && !isControlled;
    const hover = useHover(context, {
        move: false,
        enabled: enableHover,
        handleClose: interactive ? safePolygon() : undefined,
    });
    const focus = useFocus(context, { enabled: enableHover });
    const dismiss = useDismiss(context, { enabled: !isControlled });
    const enableClick = trigger === "click" && !disabled && !isControlled;
    const click = useClick(context, { enabled: enableClick });
    const role = useRole(context, { role: "tooltip" });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        click,
        dismiss,
        role,
    ]);

    const refProps = getReferenceProps();
    const floatingProps = getFloatingProps();

    return (
        <>
            <span
                ref={refs.setReference}
                {...refProps}
                className={classNames(
                    "inline-flex items-center justify-center",
                    wrapperContainerClassName,
                )}
            >
                {children}
            </span>
            <FloatingPortal root={rootElement}>
                {isOpen && !disabled && (
                    <div
                        className={classNames(
                            "z-9999 max-w-90 rounded-sm px-2 py-1 text-sm text-brand-white",
                            popupContainerClassName,
                        )}
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            backgroundColor: bgColor,
                        }}
                        {...floatingProps}
                    >
                        {title}
                        <FloatingArrow
                            ref={arrowRef}
                            context={context}
                            fill={bgColor}
                        />
                    </div>
                )}
            </FloatingPortal>
        </>
    );
}
